import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  MoreOutlined,
} from '@ant-design/icons'
import { router } from '@inertiajs/react'
import type { MenuProps } from 'antd'
import { Dropdown, Popconfirm, Space, Button } from 'antd'

export interface AdminTableActionsProps<T = any> {
  record: T
  onEdit?: (record: T) => void
  onDelete?: (record: T) => void
  onView?: (record: T) => void
  editUrl?: string | ((record: T) => string)
  viewUrl?: string | ((record: T) => string)
  showEdit?: boolean
  showDelete?: boolean
  showView?: boolean
  // Use dropdown when more than 2 actions
  useDropdown?: boolean
  deleteConfirmTitle?: string
  deleteConfirmDescription?: string
}

export function AdminTableActions<T extends { key: string }>({
  record,
  onEdit,
  onDelete,
  onView,
  editUrl,
  viewUrl,
  showEdit = true,
  showDelete = false,
  showView = false,
  useDropdown = false,
  deleteConfirmTitle = 'Delete this item?',
  deleteConfirmDescription = 'Are you sure you want to delete this item?',
}: AdminTableActionsProps<T>) {
  const handleEdit = () => {
    if (editUrl) {
      const url = typeof editUrl === 'function' ? editUrl(record) : editUrl
      router.visit(url)
    } else if (onEdit) {
      onEdit(record)
    }
  }

  const handleView = () => {
    if (viewUrl) {
      const url = typeof viewUrl === 'function' ? viewUrl(record) : viewUrl
      router.visit(url)
    } else if (onView) {
      onView(record)
    }
  }

  const handleDelete = () => {
    if (onDelete) {
      onDelete(record)
    }
  }

  // Count visible actions
  const actionCount = [showView, showEdit, showDelete].filter(Boolean).length

  // Use dropdown for 3+ actions or when explicitly requested
  if (useDropdown || actionCount >= 3) {
    const menuItems: MenuProps['items'] = []

    if (showView) {
      menuItems.push({
        key: 'view',
        icon: <EyeOutlined />,
        label: 'View',
        onClick: handleView,
      })
    }

    if (showEdit) {
      menuItems.push({
        key: 'edit',
        icon: <EditOutlined />,
        label: 'Edit',
        onClick: handleEdit,
      })
    }

    if (showDelete) {
      menuItems.push({
        key: 'delete',
        icon: <DeleteOutlined />,
        label: 'Delete',
        danger: true,
        onClick: handleDelete,
      })
    }

    return (
      <Dropdown menu={{ items: menuItems }} trigger={['click']}>
        <Button type="text" icon={<MoreOutlined />} size="small" />
      </Dropdown>
    )
  }

  // Regular inline buttons for 1-2 actions
  return (
    <Space size="small">
      {showView && (
        <Button
          type="text"
          icon={<EyeOutlined />}
          onClick={handleView}
          size="small"
        >
          View
        </Button>
      )}
      {showEdit && (
        <Button
          type="text"
          icon={<EditOutlined />}
          onClick={handleEdit}
          size="small"
        >
          Edit
        </Button>
      )}
      {showDelete && (
        <Popconfirm
          title={deleteConfirmTitle}
          description={deleteConfirmDescription}
          onConfirm={handleDelete}
          okText="Yes"
          cancelText="No"
          okButtonProps={{ danger: true }}
        >
          <Button type="text" danger icon={<DeleteOutlined />} size="small">
            Delete
          </Button>
        </Popconfirm>
      )}
    </Space>
  )
}
